import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaMessagingSettingsV1Service } from '../service';
import { TypesIO } from '../types';

export const getTypesQueryKey = (req: TypesIO['input']) => ['MessagingSettingsV1', 'Types', req] as const;
type QueryKey = ReturnType<typeof getTypesQueryKey>;

/**
 * A hook that returns a query for the `Types` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useTypesQuery = <E = unknown, D = TypesIO['output']>(
  req: TypesIO['input'],
  options?: LimitedSchemaQueryOptions<TypesIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<TypesIO['output'], E, D, QueryKey>({
    queryKey: getTypesQueryKey(req),
    queryFn: () => SchemaMessagingSettingsV1Service.Types(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `Types` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useTypesQueries = <E = unknown, D = TypesIO['output']>(
  argsArr: {
    req: TypesIO['input'];
    options?: LimitedSchemaQueryOptions<TypesIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getTypesQueryKey(req),
      queryFn: () => SchemaMessagingSettingsV1Service.Types(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useTypesQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `Types` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchTypes = () => {
  const queryClient = useQueryClient();

  const fetchTypes = async <E = unknown, D = TypesIO['output']>(
    req: TypesIO['input'],
    options?: LimitedSchemaFetchQueryOptions<TypesIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<TypesIO['output'], E, D, QueryKey>(
      getTypesQueryKey(req),
      () => SchemaMessagingSettingsV1Service.Types(req, httpOptions),
      options
    );

    return response;
  };

  return fetchTypes;
};
